import $ from 'jquery';
import '../../vendor/d3';

const { d3 } = globalThis;

type LineType = 'balance' | 'invested';

type Balance = {
  date: Date;
  balance: number;
  totalInvested: number;
  earned: number;
};

type Options = {
  balanceByDate: Balance[];
};

type Settings = Options & {
  $el: JQuery;
  marginLeft: number;
  width: number;
  height: number;
  maxBalance: number;
  maxInvested: number;
};

export function renderBalancesGraph(element: HTMLElement, options: Options): void {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const graph = new BalancesGraph($(element), options);
  graph.create();
}

const GRAPH_HEIGHT = 375;
const TOOLTIP_CLASS = '.PerformanceBalanceGraphTooltip';
const VERTICAL_BUFFER = 1.15;
const TRANSITION_DURATION = 800;
const Y_TICKS = 5;
const MARGIN_TOP = 10;
const MARGIN_RIGHT = 1;
const MARGIN_BOTTOM = 30;

class BalancesGraph {
  private settings: Settings;

  private scales: {
    x: d3.time.Scale<number, number>;
    y: d3.scale.Linear<number, number>;
  };

  private generators: {
    xAxis: d3.svg.Axis;
    yAxis: d3.svg.Axis;
    line: {
      zero: d3.svg.Line<Balance>;
      invested: d3.svg.Line<Balance>;
      balance: d3.svg.Line<Balance>;
    };
    area: {
      zero: d3.svg.Area<Balance>;
      balance: d3.svg.Area<Balance>;
    };
  };

  private svg: d3.Selection<HTMLElement>;

  constructor($el: JQuery, { balanceByDate }: Options) {
    const marginLeft = Math.max(50, $el.width()! * 0.08);
    const width = $el.width()! - marginLeft - MARGIN_RIGHT;
    const height = Math.min(0.75 * width, GRAPH_HEIGHT) - MARGIN_TOP - MARGIN_BOTTOM;
    const maxBalance = d3.max(balanceByDate, d => d.balance);
    const maxInvested = d3.max(balanceByDate, d => d.totalInvested);

    this.settings = { $el, marginLeft, width, height, maxBalance, maxInvested, balanceByDate };
  }

  private hasData(): boolean {
    return this.settings.balanceByDate.length > 0;
  }

  public create(): void {
    this.setUpScales();
    this.setUpGenerators();
    this.createLayout();

    if (!this.hasData()) {
      this.showZeroState();
    } else {
      this.drawData();
      this.drawHoverBar();
      this.initHover();
    }
  }

  private showZeroState(): void {
    this.settings.$el.parent().find('.PerformanceBalanceGraph-zeroState').addClass('u-displayFlex');
  }

  private setUpScales(): void {
    this.scales = {
      x: d3.time
        .scale()
        .domain(d3.extent(this.settings.balanceByDate, d => d.date))
        .range([0, this.settings.width]),
      y: d3.scale
        .linear()
        .domain([0, VERTICAL_BUFFER * d3.max([this.settings.maxBalance, this.settings.maxInvested])])
        .range([this.settings.height, 0])
        .nice(),
    };
  }

  private setUpGenerators(): void {
    this.generators = {
      xAxis: d3.svg
        .axis()
        .scale(this.scales.x)
        .tickSize(10, 0)
        .tickValues(this.scales.x.domain())
        .tickPadding(6)
        .orient('bottom')
        .tickFormat((date: Date) => d3.time.format('%b %e, %Y')(date)),
      yAxis: d3.svg
        .axis()
        .scale(this.scales.y)
        .tickSize(-1 * this.settings.width, 0)
        .ticks(Y_TICKS)
        .tickPadding(6)
        .tickFormat((d: number) => d3.format('$,s')(d))
        .orient('left'),
      line: {
        zero: d3.svg
          .line<Balance>()
          .x(d => this.scales.x(d.date))
          .y(d => this.scales.y(0)),
        invested: d3.svg
          .line<Balance>()
          .x(d => this.scales.x(d.date))
          .y(d => this.scales.y(d.totalInvested)),
        balance: d3.svg
          .line<Balance>()
          .x(d => this.scales.x(d.date))
          .y(d => this.scales.y(d.balance)),
      },
      area: {
        zero: d3.svg
          .area<Balance>()
          .x(d => this.scales.x(d.date))
          .y(d => this.scales.y(0)),
        balance: d3.svg
          .area<Balance>()
          .x(d => this.scales.x(d.date))
          .y0(this.settings.height)
          .y1(d => this.scales.y(d.balance)),
      },
    };
  }

  private createLayout(): void {
    this.svg = (d3.select(`#${this.settings.$el.attr('id')}`) as d3.Selection<HTMLElement>)
      .attr('width', this.settings.width + this.settings.marginLeft + MARGIN_RIGHT)
      .attr('height', this.settings.height + MARGIN_TOP + MARGIN_BOTTOM)
      .on('mousemove', () => this.updateHover())
      .on('mouseleave', () => this.hideHover())
      .append('g')
      .attr('transform', `translate(${this.settings.marginLeft},${MARGIN_TOP})`);

    this.svg
      .append('g')
      .attr('class', 'PerformanceBalanceGraph-axis PerformanceBalanceGraph-axis--x')
      .attr('transform', `translate(0,${this.settings.height})`)
      .call(this.generators.xAxis)
      .selectAll('text')
      .style('text-anchor', (_tick, idx) => (idx % 2 === 0 ? 'start' : 'end'));

    this.svg
      .append('g')
      .attr('class', 'PerformanceBalanceGraph-axis PerformanceBalanceGraph-axis--y')
      .attr('transform', `translate(${0} , 0)`)
      .call(this.generators.yAxis);

    this.svg.selectAll('g.tick').attr('class', 'tick PerformanceBalanceGraph-axisTick PerformanceBalanceGraph-label');

    this.svg
      .selectAll('.PerformanceBalanceGraph-axis--y g.tick line')
      .style('stroke-dasharray', (line, idx) => (idx === 0 ? 'none' : '5'));
  }

  private drawData(): void {
    this.drawArea('balance');
    this.drawArea('invested');
    this.drawLine('balance');
    this.drawLine('invested');
    this.drawCircle('balance');
    this.drawCircle('invested');
    this.updateDrawnData('balance');
    this.updateDrawnData('invested');
  }

  private updateDrawnData(type: LineType): void {
    this.updateLine(type, type);
    this.updateArea(type, type);
  }

  private drawArea(type: LineType): void {
    this.svg
      .append('path')
      .datum(this.settings.balanceByDate)
      .attr('class', `PerformanceBalanceGraph-area PerformanceBalanceGraph-area--${type}`)
      .attr('d', this.generators.area.zero);
  }

  private updateArea(type: LineType, generator: LineType): void {
    d3.select(`.PerformanceBalanceGraph-area.PerformanceBalanceGraph-area--${type}`)
      .datum(this.settings.balanceByDate)
      .transition()
      .duration(TRANSITION_DURATION)
      .attr(
        'd',
        // @ts-expect-error `this.generators.area` doesn't have a value for `invested`
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.generators.area[generator]
      );
  }

  private drawLine(type: LineType): void {
    this.svg
      .append('path')
      .datum(this.settings.balanceByDate)
      .attr('class', `PerformanceBalanceGraph-line PerformanceBalanceGraph-line--${type}`)
      .attr('d', this.generators.line.zero);
  }

  private updateLine(type: LineType, generator: LineType): void {
    d3.select(`.PerformanceBalanceGraph-line.PerformanceBalanceGraph-line--${type}`)
      .datum(this.settings.balanceByDate)
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('d', this.generators.line[generator]);
  }

  private drawCircle(type: LineType): void {
    this.svg
      .append('circle')
      .attr('r', 3)
      .attr(
        'class',
        `PerformanceBalanceGraph-hoverable PerformanceBalanceGraph-circle PerformanceBalanceGraph-circle--${type}`
      );
  }

  private drawHoverBar(): void {
    this.svg.append('path').attr('class', 'PerformanceBalanceGraph-hoverable PerformanceBalanceGraph-bar');
  }

  private initHover(): void {
    const lastPoint = this.settings.balanceByDate.at(-1)!;
    this.updateHoverBar(lastPoint.date);
    this.updateTooltip(lastPoint);
    this.updateCircles(lastPoint);
    this.hideHover();
  }

  private updateHoverBar(date: Date): void {
    d3.select(`#${this.settings.$el.attr('id')} .PerformanceBalanceGraph-hoverable.PerformanceBalanceGraph-bar`)
      .classed('is-hovered', true)
      .attr('d', `M${this.scales.x(date)},${this.settings.height}L${this.scales.x(date)},${0}`);
  }

  private updateTooltip(point: Balance): void {
    const $tooltip = $(TOOLTIP_CLASS);
    const toolWidth = $tooltip.outerWidth()!;
    const offset = this.scales.x(point.date) - toolWidth;
    const leftMargin = this.settings.marginLeft;

    $tooltip.find('.PerformanceBalanceGraphTooltip-date').text(dateFormat(point.date));
    $tooltip.find('.PerformanceBalanceGraphTooltip-segmentValue--balance').text(currencyFormat(point.balance));
    $tooltip.find('.PerformanceBalanceGraphTooltip-segmentValue--invested').text(currencyFormat(point.totalInvested));
    if (point.earned > 0) {
      $tooltip
        .find('.PerformanceBalanceGraphTooltip-segmentValue--earned')
        .text(`+${currencyFormat(point.earned)}`)
        .addClass('u-color-bmtTeal90');
    } else {
      $tooltip
        .find('.PerformanceBalanceGraphTooltip-segmentValue--earned')
        .text(currencyFormat(point.earned))
        .removeClass('u-color-bmtTeal90');
    }
    $tooltip
      .css({
        left: this.scales.x(point.date) + leftMargin - (offset > 0 ? toolWidth - 1 : 1) + 16,
      })
      .addClass('is-hovered');
  }

  private updateCircles(point: Balance): void {
    d3.selectAll('.PerformanceBalanceGraph-hoverable.PerformanceBalanceGraph-circle')
      .classed('is-hovered', true)
      .attr('cx', this.scales.x(point.date));
    d3.select('.PerformanceBalanceGraph-circle.PerformanceBalanceGraph-circle--balance').attr(
      'cy',
      this.scales.y(point.balance)
    );
    d3.select('.PerformanceBalanceGraph-circle.PerformanceBalanceGraph-circle--invested').attr(
      'cy',
      this.scales.y(point.totalInvested)
    );
  }

  private updateHover(): void {
    const container = d3.select(`#${this.settings.$el.attr('id')}`);
    const mouseX = d3.mouse(container.node())[0] - this.settings.marginLeft;

    if (mouseX >= 0 && mouseX <= this.settings.width) {
      const exactDate = this.scales.x.invert(mouseX);
      const point = this.getClosestDataPoint(exactDate);
      if (point) {
        this.updateHoverBar(point.date);
        this.updateTooltip(point);
        this.updateCircles(point);
      }
    } else {
      this.hideHover();
    }
  }

  private getClosestDataPoint(date: Date): Balance {
    const bisect = d3.bisector<Balance, Date>(d => d.date).right;
    const index = bisect(this.settings.balanceByDate, date);
    const d0 = this.settings.balanceByDate[index - 1];
    const d1 = this.settings.balanceByDate[index];

    if (d1 === undefined) {
      return d0;
    }
    if (d0 === undefined) {
      return d1;
    }

    // @ts-expect-error Right hand of arithmatic must be a number
    return date - d0.date > d1.date - date ? d1 : d0;
  }

  private hideHover(): void {
    d3.selectAll('.PerformanceBalanceGraph-hoverable').classed('is-hovered', false);
  }
}

function dateFormat(date: Date): string {
  return d3.time.format('%B %d, %Y')(date);
}

function currencyFormat(amount: number): string {
  return d3.format('$,.2f')(amount);
}
